import { Skeleton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { match } from 'ts-pattern';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Text } from '~/domains/design-system/Text';
import Image from '~/domains/common/components/Image/Image';
import ImagePlaceholder from '~/domains/common/components/Image/ImagePlaceholder';
import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';
import { getCurrentUserTimezoneAbbreviation } from '~/domains/contest/domains/common/utils/playerCardDisplay';
import {
  getGameTeeTimes,
  getSlateWithGames,
} from '~/domains/games/services/games-service.service.api';

import { useContestContext } from '../../../common/context/ContestContext/ContestContext';
import { STALE_TIME } from '~/domains/contest/constants/query';

import { useStyles } from './TeeTimesModal.styles';
import { IconClock } from '@tabler/icons-react';

const formatTeeTime = (
  teetime: string,
  shouldShowTimezone: boolean,
  timezoneAbbreviation: string | undefined
): string => {
  const date = new Date(teetime);
  if (Number.isNaN(date.getTime())) return teetime;
  const formattedTime = format(date, 'EEE h:mm a');
  return shouldShowTimezone && timezoneAbbreviation
    ? `${formattedTime} ${timezoneAbbreviation}`
    : formattedTime;
};

type TeeTimesEmptyStateProps = {
  slateName: string;
};

function TeeTimesEmptyState({ slateName }: TeeTimesEmptyStateProps) {
  const { t } = useTranslation('contest');
  const { classes, theme } = useStyles();

  return (
    <div className={classes.emptyState}>
      <IconClock size={60} color={theme.colors.gray2[3]} />
      <Text variant="subhead-x-large" c={theme.colors.gray2[4]}>
        {t('gamecenter.player.common.teeTimesModal.emptyState.title')}
      </Text>
      <Text variant="title-medium" c={theme.colors.gray2[4]}>
        {t('gamecenter.player.common.teeTimesModal.emptyState.description', { slateName })}
      </Text>
    </div>
  );
}

type GamecenterTeeTimesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  slateName: string;
  slateId?: string;
};

function GamecenterTeeTimesModal({
  isOpen,
  onClose,
  slateName,
  slateId,
}: GamecenterTeeTimesModalProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('contest');
  const { contest } = useContestContext();
  const shouldShowTimezone = !!contest?.settings?.customContestSettings?.showTimezone;
  const timezoneAbbreviation = useMemo(() => getCurrentUserTimezoneAbbreviation(), []);

  const slateWithGamesQuery = useQuery({
    queryKey: ['slate-with-games', slateId],
    queryFn: () => getSlateWithGames(slateId),
    enabled: isOpen && !!slateId,
    staleTime: STALE_TIME,
  });
  const gameId = slateWithGamesQuery.data?.games?.[0]?.id ?? slateWithGamesQuery.data?.gameIds?.[0];

  const teeTimesQuery = useQuery({
    queryKey: ['game-teetimes', gameId, 1],
    queryFn: () => getGameTeeTimes({ gameId, round: 1 }),
    enabled: isOpen && !!gameId,
    staleTime: STALE_TIME,
  });

  const teeTimes = teeTimesQuery.data?.teetimes ?? [];
  const isEmpty = !gameId || !teeTimes.length;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      type={{ desktop: 'modal', mobile: 'drawer' }}
      className={classes.modal}
    >
      <DialogBody className={classes.modalBody}>
        <div className={classes.modalHeader}>
          <Text variant="headline-large">{t('gamecenter.player.common.teeTimesModal.title')}</Text>
          <Text variant="subhead-small" c="gray2.3">
            {slateName}
          </Text>
        </div>
        {match({
          isFetching: slateWithGamesQuery.isFetching || teeTimesQuery.isFetching,
          isEmptyOrError: isEmpty || !!teeTimesQuery.error,
        })
          .returnType<React.ReactNode>()
          .with({ isFetching: true }, () => (
            <div className={classes.itemsListLoadingContainer}>
              <Skeleton height={32} radius="sm" />
              <Skeleton height={32} radius="sm" />
              <Skeleton height={32} radius="sm" />
              <Skeleton height={32} radius="sm" />
              <Skeleton height={32} radius="sm" />
            </div>
          ))
          .with({ isEmptyOrError: true }, () => <TeeTimesEmptyState slateName={slateName} />)
          .otherwise(() => (
            <div className={classes.listFrame}>
              <div className={classes.itemsHeader}>
                <Text variant="subhead-x-small" c="gray2.3" tt="uppercase">
                  {t('gamecenter.player.common.teeTimesModal.tableHead.player')}
                </Text>
                <Text variant="subhead-x-small" c="gray2.3" tt="uppercase">
                  {t('gamecenter.player.common.teeTimesModal.tableHead.teeTime')}
                </Text>
              </div>
              <div className={classes.itemsList}>
                {teeTimes.map((teeTimeItem) => (
                  <div className={classes.item} key={teeTimeItem.playerId}>
                    <div className={classes.playerColumn}>
                      <Image
                        src={getTeamOrPlayerImageUrl(contest.league.alias, teeTimeItem.playerId)}
                        alt={teeTimeItem.playerName}
                        width={24}
                        height={24}
                        fallbackComponent={<ImagePlaceholder width={24} height={24} />}
                        className={classes.playerImage}
                      />
                      <Text variant="subhead-small" c="gray2.6" truncate>
                        {teeTimeItem.playerName}
                      </Text>
                    </div>
                    <Text variant="subhead-small" c="gray2.4">
                      {formatTeeTime(teeTimeItem.teetime, shouldShowTimezone, timezoneAbbreviation)}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </DialogBody>
    </Dialog>
  );
}

export default GamecenterTeeTimesModal;
